import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { z } from '@customerio/hydra-validation';
import { useZodForm, useWatch } from '@/components/form';
import { isValidEmail } from '@/utils/email';
import { sentryCaptureException } from '@/utils/sentry';
import { getOrCreateSignupAnonymousId } from '@/services/signup/anonymous-id';
import { submitStartupApplication, type SubmitStartupApplicationArgs } from '@/services/signup/api';
import { useSignupFlow } from '../signup-flow';
import { companyInfoSubmittedProps, SignupAnalyticsEvent } from '../utils/analytics';
import { getLandingUrlParams } from '../utils/landing-params';
import { INCUBATOR_VC_NAMES } from '../utils/startup-incubator-options';
import { isPotentiallyValidUrl } from '../utils/is-potentially-valid-url';
import { mapStartupFields } from '../utils/startup-fields';
import { resolveStartSignupErrorMessage } from '../utils/start-signup-error-message';
import { validatePhoneField } from '../utils/phone-country-codes';
import { withFlowWriteThrough } from './flow-write-through';

/** Rejects names that smuggle a URL (e.g. "win.customer.io"). */
const URL_IN_NAME_REGEX = /\S+\.\w{2,}/;

/** A headcount: any finite, non-negative number. `Number('')` is 0, so blank is rejected first. */
function isNonNegativeNumber(value: string): boolean {
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) && parsed >= 0;
}

/**
 * `isIncubatorVcHidden` skips the incubator requirement: the field is hidden
 * exactly when the landing `utm_partner` prefilled it, so requiring it would
 * gate submit on a value the visitor can't see or fix.
 */
function makeStartupFormSchema(isIncubatorVcHidden: boolean) {
  return z
    .object({
      firstName: z
        .string()
        .refine((value) => value.trim() !== '', "First name can't be blank")
        .refine((value) => !URL_IN_NAME_REGEX.test(value), 'First name cannot contain a URL'),
      lastName: z.string().refine((value) => !URL_IN_NAME_REGEX.test(value), 'Last name cannot contain a URL'),
      email: z.string().refine(isValidEmail, 'Please enter a valid email address'),
      phoneCountry: z.string(),
      phone: z.string(),
      companyName: z
        .string()
        .refine((value) => value.trim() !== '', 'Enter a startup name')
        .refine((value) => value.length <= 191, 'Startup name must be 191 characters or fewer'),
      companyLocation: z.string().min(1, 'Select your company location'),
      companyUrl: z
        .string()
        .refine((value) => value.trim() !== '', 'Enter a startup website')
        .refine((value) => value.trim() === '' || isPotentiallyValidUrl(value), 'Enter a valid URL, e.g. example.com'),
      // Validated numerically, not just for presence: the control is a text
      // field, so nothing upstream of here guarantees `company_size` is a
      // number. (Pluma's NumberField used to filter the keystrokes — a weaker
      // guarantee, since it never reached the submitted payload.)
      startupSize: z
        .string()
        .refine((value) => value.trim() !== '', 'Enter a startup size')
        .refine((value) => isNonNegativeNumber(value), 'Enter a startup size as a number'),
      startupFunding: z.string().min(1, 'Select a funding stage'),
      incubatorVc: isIncubatorVcHidden
        ? z.string()
        : z.string().min(1, 'Select an incubator, VC firm, or startup partner'),
      heardAboutUs: z.string().min(1, 'Select how you heard about us'),
      businessDescription: z
        .string()
        .refine((value) => value.trim() !== '', 'Tell us a little about your business')
        // Code points, not UTF-16 units — an emoji-heavy pitch counts fairly.
        .refine((value) => Array.from(value).length <= 2000, 'Business description must be 2,000 characters or fewer'),
    })
    .superRefine((data, ctx) => {
      const phoneResult = validatePhoneField(data.phoneCountry, data.phone);
      if (phoneResult !== true) {
        ctx.addIssue({ code: 'custom', path: ['phone'], message: phoneResult });
      }
    });
}

export type StartupFormValues = z.infer<ReturnType<typeof makeStartupFormSchema>>;

function startupApplicationBody(values: StartupFormValues): SubmitStartupApplicationArgs {
  // The mapper's optional `referral_id` is nullable (the register schema);
  // the application accepts only a string, and this flow never has one.
  const { referral_id: _referralId, ...startupFields } = mapStartupFields(values);
  return {
    email: values.email,
    first_name: values.firstName,
    last_name: values.lastName,
    company_name: values.companyName,
    website_url: values.companyUrl,
    ...startupFields,
  };
}

/** The button unlocks on presence, not validity; a submit attempt surfaces errors. */
const REQUIRED_FIELDS = [
  'email',
  'firstName',
  'companyName',
  'companyUrl',
  'companyLocation',
  'startupFunding',
  'incubatorVc',
  'heardAboutUs',
  'businessDescription',
  'phone',
  'startupSize',
] as const;

export function useStartupForm() {
  const {
    formValues,
    patchFormValues,
    setOnboardingRole,
    utmPartner,
    effectiveSignupType,
    beginSignup,
    trackSignupEvent,
  } = useSignupFlow();
  const snackbars = useSnackbars();

  const isIncubatorVcHidden = utmPartner != null && INCUBATOR_VC_NAMES.includes(utmPartner);

  const form = useZodForm({
    schema: useMemo(() => makeStartupFormSchema(isIncubatorVcHidden), [isIncubatorVcHidden]),
    defaultValues: {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email,
      phoneCountry: formValues.phoneCountry,
      phone: formValues.phone,
      companyName: formValues.companyName,
      companyLocation: formValues.companyLocation,
      companyUrl: formValues.companyUrl,
      startupSize: formValues.startupSize,
      startupFunding: formValues.startupFunding,
      // The landing partner ships without the visitor re-selecting it.
      incubatorVc: isIncubatorVcHidden ? utmPartner : formValues.incubatorVc,
      heardAboutUs: formValues.heardAboutUs,
      businessDescription: formValues.businessDescription,
    },
  });

  // Every applicant is recorded as founder/CEO — the startup flow skips the
  // company step, which is where the role question otherwise lives.
  const hasEnteredForm = useRef(false);
  useEffect(() => {
    if (hasEnteredForm.current) {
      return;
    }
    hasEnteredForm.current = true;
    setOnboardingRole('founder_ceo');
    if (isIncubatorVcHidden) {
      patchFormValues({ incubatorVc: utmPartner });
    }
  }, [setOnboardingRole, patchFormValues, isIncubatorVcHidden, utmPartner]);

  const watched = useWatch({ control: form.control, name: REQUIRED_FIELDS });
  const allRequiredFieldsFilled = REQUIRED_FIELDS.every((field, index) => {
    if (field === 'incubatorVc' && isIncubatorVcHidden) {
      return true;
    }
    return (watched[index] ?? '').trim() !== '';
  });

  // The email whose application Marketing has already received. A failed
  // `beginSignup` leaves the visitor on this form, so a retry re-runs the
  // handler — and the application POST carries no idempotency key, so an
  // unguarded retry would leave a duplicate row. Recorded only once the POST
  // *succeeds*: a failed application must still be retried, or the lead is lost
  // for a signup that later goes through. Keyed by email, because correcting a
  // rejected address is a new applicant rather than a retry.
  const appliedEmailRef = useRef<string | null>(null);

  const onSubmit = form.handleSubmit(async (values: StartupFormValues) => {
    if (!allRequiredFieldsFilled) {
      // Enter-key submits bypass the disabled button.
      return;
    }

    if (appliedEmailRef.current !== values.email) {
      trackSignupEvent(
        SignupAnalyticsEvent.StartupStepSubmitted,
        companyInfoSubmittedProps({
          signupType: effectiveSignupType,
          companyName: values.companyName,
          website: values.companyUrl,
          haveWebsite: true,
        }),
      );

      // Pre-creation, email-keyed application event for Marketing. The backend
      // answers 204 regardless and persists nothing, so a failure is reported,
      // never allowed to block the signup.
      try {
        await submitStartupApplication(startupApplicationBody(values), {
          landingUrlParams: getLandingUrlParams(),
          anonymousId: getOrCreateSignupAnonymousId(),
        });
        appliedEmailRef.current = values.email;
      } catch (error) {
        sentryCaptureException(error, { level: 'error', extra: { context: 'startup_application_pre_creation' } });
      }
    }

    try {
      await beginSignup(values.email);
    } catch (error) {
      snackbars.create({
        variant: 'error',
        id: 'signup.error',
        message: resolveStartSignupErrorMessage(error),
      });
    }
  });

  /** `registerPluma` plus write-through, so navigation remounts restore answers. */
  const registerStartupField = useCallback(
    (name: (typeof REQUIRED_FIELDS)[number] | 'lastName') =>
      withFlowWriteThrough(form.registerPluma(name), name, patchFormValues),
    [form, patchFormValues],
  );

  return {
    form,
    registerStartupField,
    isIncubatorVcHidden,
    isSubmitDisabled: !allRequiredFieldsFilled || form.formState.isSubmitting,
    isSubmitting: form.formState.isSubmitting,
    onSubmit,
    patchFormValues,
  };
}
